import React, { useState } from "react";
import { useTeacherId } from "../hooks/useTeacherId";
import { useTeacherClassrooms } from "../hooks/useTeacherClassrooms";
import { TaskGeneratorModal } from "./TaskGeneratorModal";
import type { TaskType, TaskConfig } from "../lib/taskEngine";

interface TaskCreatorButtonProps {
  type: TaskType;
  title: string;
  description?: string;
  config: TaskConfig;
  variant?: "bottom" | "sidebar" | "compact" | "studio" | "link" | "immersive" | "serenity";
}

export const TaskCreatorButton: React.FC<TaskCreatorButtonProps> = ({
  type,
  title,
  description,
  config,
  variant = "bottom",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { teacherId, teacherName } = useTeacherId();
  const { classrooms, members } = useTeacherClassrooms(teacherId, teacherName);

  const buttonClass =
    variant === "immersive"
      ? "text-2xl font-sans text-[#c4b5fd] hover:text-emerald-200 transition-colors tracking-wide shrink-0 whitespace-nowrap"
      : variant === "serenity" || variant === "link"
      ? "text-sm font-sans text-[#c4b5fd] hover:text-[#e9e0ff] transition-colors flex items-center gap-2 text-left"
      : variant === "bottom"
      ? "px-3 h-8 rounded-md text-xs font-sans font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-all shrink-0"
      : variant === "studio"
      ? "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all shrink-0 hover:brightness-110"
      : variant === "compact"
      ? "w-8 h-8 rounded-full flex items-center justify-center bg-[#8B7ED8] text-white hover:bg-[#7A6EC8] transition-all shadow-sm"
      : "w-full py-2 rounded-md text-xs font-sans font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all";

  const studioStyle =
    variant === "studio"
      ? { backgroundColor: "#8B7ED8", boxShadow: "0 0 16px rgba(139,126,216,0.35)" }
      : undefined;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={buttonClass}
        style={studioStyle}
        title="Crear tarea para estudiantes"
      >
        {variant === "compact" ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
        ) : variant === "studio" ? (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            Crear tarea
          </>
        ) : variant === "link" || variant === "serenity" || variant === "immersive" ? (
          <>Crear tarea pedagógica →</>
        ) : (
          "📋 Crear tarea"
        )}
      </button>
      <TaskGeneratorModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        teacherId={teacherId}
        teacherName={teacherName}
        type={type}
        title={title}
        description={description}
        config={config}
        classrooms={classrooms}
        classroomMembers={members}
      />
    </>
  );
};
